import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { FirebaseAuthService } from '../services/firebaseAuthService';
import { 
  Plus, Search, Edit, Trash2, Eye, CheckCircle2, XCircle, Power, Filter, 
  ChevronDown, ChevronUp, User, Building2, Phone, Mail, FileText, Calendar, X, Hash,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, ArrowUpDown, SlidersHorizontal, RefreshCw
} from 'lucide-react';
import { PessoasForm } from './PessoasForm';
import { PessoaDetailsModal } from '../components/pessoa/PessoaDetailsModal';
import { pessoaService, isPessoaAtiva } from '../services/pessoaService';
import { ProtectedField } from '../components/common/ProtectedField';

function formatDocumento(doc?: string | null): string {
  if (!doc) return '-';
  const clean = doc.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (clean.length === 14) {
    return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return doc;
}

function formatPhone(phone?: string | null): string {
  if (!phone) return '-';
  const clean = phone.replace(/\D/g, '');
  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone;
}

function getPageNumbers(currentPage: number, totalPages: number, maxVisible = 10) {
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = start + maxVisible - 1;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages = [];
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
}

export function Pessoas() {
  const [pessoas, setPessoas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativos' | 'inativos'>('all');
  const [tipoPessoaFilter, setTipoPessoaFilter] = useState<'all' | 'pf' | 'pj'>('all');
  const [contatoFilter, setContatoFilter] = useState<'all' | 'com_fone' | 'com_email'>('all');
  const [sortOrder, setSortOrder] = useState<'nome_asc' | 'nome_desc' | 'id_desc' | 'id_asc'>('nome_asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);

  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  
  // Expanded rows state for accordion
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);

  useEffect(() => {
    fetchPessoas();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, tipoPessoaFilter, contatoFilter, sortOrder]);

  async function fetchPessoas() {
    try {
      setLoading(true);
      setErrorMsg(null);
      const data = await pessoaService.getAll();
      setPessoas(data || []);
    } catch (error: any) {
      console.error('Error fetching pessoas:', error);
      if (error?.code === '42501' || error?.message?.includes('permission denied')) {
        setErrorMsg('Acesso restrito: Você não possui permissão para consultar o cadastro de pessoas.');
      } else {
        setErrorMsg('Erro ao carregar pessoas.');
      }
    } finally {
      setLoading(false);
    }
  }

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleOpenModal = (pessoa?: any) => {
    if (pessoa) {
      setEditingId(pessoa.id);
    } else {
      setEditingId(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleOpenDetails = (id: number) => {
    setViewingId(id);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setViewingId(null);
  };

  const handleEditFromDetails = (id: number) => {
    handleCloseDetails();
    setEditingId(id);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    fetchPessoas();
    handleCloseModal();
  };

  const handleToggleAtivo = async (pessoa: any) => {
    const atualAtivo = isPessoaAtiva(pessoa);
    const acao = atualAtivo ? 'DESATIVAR' : 'ATIVAR';
    const confirmMessage = `Deseja realmente ${acao} o cadastro de "${pessoa.nome}" no banco de dados?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      setTogglingId(pessoa.id);
      await pessoaService.toggleAtivo(pessoa.id, !atualAtivo);
      await fetchPessoas();
    } catch (error: any) {
      console.error('Error toggling pessoa status:', error);
      alert(`Erro ao ${acao.toLowerCase()} pessoa no banco de dados: ` + error.message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir ${nome}?`)) return;

    try {
      const { error } = await supabase.from('pessoa').delete().eq('id', id);
      if (error) throw error;
      await fetchPessoas();
    } catch (error: any) {
      console.error('Error deleting pessoa:', error);
      alert('Erro ao excluir: ' + error.message);
    }
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTipoPessoaFilter('all');
    setContatoFilter('all');
    setSortOrder('nome_asc');
  };

  const hasActiveFilters = 
    searchTerm !== '' || 
    statusFilter !== 'all' || 
    tipoPessoaFilter !== 'all' || 
    contatoFilter !== 'all' ||
    sortOrder !== 'nome_asc';

  const filteredPessoas = pessoas.filter(p => {
    const matchesSearch = 
      p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.nome_fantasia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.documento?.includes(searchTerm) ||
      p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.fone?.includes(searchTerm);

    const ativa = isPessoaAtiva(p);
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'ativos' && ativa) ||
      (statusFilter === 'inativos' && !ativa);

    const docClean = p.documento ? p.documento.replace(/\D/g, '') : '';
    const isPJ = docClean.length > 11;
    const matchesTipo =
      tipoPessoaFilter === 'all' ||
      (tipoPessoaFilter === 'pf' && !isPJ) ||
      (tipoPessoaFilter === 'pj' && isPJ);

    const matchesContato =
      contatoFilter === 'all' ||
      (contatoFilter === 'com_fone' && !!p.fone) ||
      (contatoFilter === 'com_email' && !!p.email);

    return matchesSearch && matchesStatus && matchesTipo && matchesContato;
  }).sort((a, b) => {
    if (sortOrder === 'nome_asc') return (a.nome || '').localeCompare(b.nome || '');
    if (sortOrder === 'nome_desc') return (b.nome || '').localeCompare(a.nome || '');
    if (sortOrder === 'id_desc') return (b.id || 0) - (a.id || 0);
    if (sortOrder === 'id_asc') return (a.id || 0) - (b.id || 0);
    return 0;
  });

  const totalAtivos = pessoas.filter(isPessoaAtiva).length;
  const totalInativos = pessoas.length - totalAtivos;

  // Pagination metrics
  const totalRecords = filteredPessoas.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  const paginatedPessoas = filteredPessoas.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="space-y-6">
      {/* Header section */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pessoas</h1>
          <p className="mt-1 text-sm text-slate-500">
            Gerencie clientes, fornecedores e colaboradores com controle de status ativo/desativo no banco de dados.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center gap-2">
          <ProtectedField permission="manage_people" hideMode="remove">
            <button
              type="button"
              onClick={() => handleOpenModal()}
              className="inline-flex items-center justify-center rounded-lg border border-transparent bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors cursor-pointer"
            >
              <Plus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Nova Pessoa
            </button>
          </ProtectedField>
        </div>
      </div>

      {/* Main card */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-slate-200">
        {/* Filter bar - Line 1: Search & Status Pills */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="relative rounded-lg shadow-2xs max-w-md w-full">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="h-4 w-4 text-slate-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              name="search"
              id="search"
              className="block w-full rounded-lg border-slate-300 pl-9 pr-8 focus:border-red-500 focus:ring-red-500 text-sm py-2 border outline-none bg-white text-slate-900 placeholder:text-slate-400"
              placeholder="Buscar por nome, fantasia, CPF/CNPJ, fone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-medium">
            <Filter className="w-3.5 h-3.5 text-slate-500 ml-1.5 hidden sm:inline" />
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer font-medium ${
                statusFilter === 'all'
                  ? 'bg-white text-slate-900 font-semibold shadow-2xs border border-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Todos ({pessoas.length})
            </button>
            <button
              onClick={() => setStatusFilter('ativos')}
              className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer font-medium ${
                statusFilter === 'ativos'
                  ? 'bg-emerald-600 text-white font-semibold shadow-2xs'
                  : 'text-emerald-700 hover:bg-emerald-50'
              }`}
            >
              Ativos ({totalAtivos})
            </button>
            <button
              onClick={() => setStatusFilter('inativos')}
              className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer font-medium ${
                statusFilter === 'inativos'
                  ? 'bg-rose-600 text-white font-semibold shadow-2xs'
                  : 'text-rose-700 hover:bg-rose-50'
              }`}
            >
              Inativos ({totalInativos})
            </button>
          </div>
        </div>

        {/* Filter bar - Line 2: Advanced Dropdown Filters */}
        <div className="px-4 py-2.5 bg-slate-100/70 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-500 uppercase tracking-wider text-[11px] flex items-center gap-1">
              <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
              Filtros:
            </span>

            {/* Tipo Pessoa Dropdown */}
            <select
              value={tipoPessoaFilter}
              onChange={(e) => setTipoPessoaFilter(e.target.value as any)}
              className="bg-white border border-slate-300 text-slate-700 text-xs rounded-lg px-2.5 py-1.5 focus:border-red-500 focus:outline-none shadow-2xs cursor-pointer font-medium"
            >
              <option value="all">Tipo: Todos (PF e PJ)</option>
              <option value="pf">Pessoa Física (CPF)</option>
              <option value="pj">Pessoa Jurídica (CNPJ)</option>
            </select>

            {/* Contato Dropdown */}
            <select
              value={contatoFilter}
              onChange={(e) => setContatoFilter(e.target.value as any)}
              className="bg-white border border-slate-300 text-slate-700 text-xs rounded-lg px-2.5 py-1.5 focus:border-red-500 focus:outline-none shadow-2xs cursor-pointer font-medium"
            >
              <option value="all">Contato: Todos</option>
              <option value="com_fone">Com Telefone Cadastrado</option>
              <option value="com_email">Com E-mail Cadastrado</option>
            </select>

            {/* Ordenação Dropdown */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-lg px-2 py-1 shadow-2xs">
              <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as any)}
                className="bg-transparent text-slate-700 text-xs font-medium focus:outline-none cursor-pointer"
              >
                <option value="nome_asc">Ordem: Nome (A - Z)</option>
                <option value="nome_desc">Ordem: Nome (Z - A)</option>
                <option value="id_desc">Ordem: Recentes (Maior ID)</option>
                <option value="id_asc">Ordem: Antigos (Menor ID)</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-md transition-colors cursor-pointer font-medium border border-red-200"
              >
                <RefreshCw className="w-3 h-3" />
                Limpar Filtros
              </button>
            )}
          </div>

          <div className="text-slate-500 font-medium">
            Exibindo <span className="font-bold text-slate-800">{filteredPessoas.length}</span> de <span className="font-bold text-slate-800">{pessoas.length}</span> cadastros
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {errorMsg && (
            <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 m-4 rounded-lg">
              <p className="font-medium">Erro ao carregar dados</p>
              <p className="text-sm">{errorMsg}</p>
            </div>
          )}
          
          <table className="w-full divide-y divide-slate-200 text-left table-auto">
            <thead className="bg-slate-50/80 text-slate-600">
              <tr>
                <th scope="col" className="w-10 px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                  <span className="sr-only">Expandir</span>
                </th>
                <th scope="col" className="px-3 py-3 text-xs font-semibold uppercase tracking-wider min-w-[200px] max-w-xs">
                  Nome / Razão Social
                </th>
                <th scope="col" className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">
                  Contato
                </th>
                <th scope="col" className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-slate-500">
                    <div className="flex justify-center items-center gap-3">
                      <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="font-medium text-slate-700">Carregando listagem de pessoas...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPessoas.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-slate-500">
                    <div className="max-w-sm mx-auto space-y-2">
                      <User className="w-10 h-10 text-slate-300 mx-auto" />
                      <p className="font-semibold text-slate-800">Nenhuma pessoa encontrada</p>
                      <p className="text-xs text-slate-500">Tente buscar por outro termo ou remova os filtros ativos.</p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors cursor-pointer"
                        >
                          Limpar Todos os Filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedPessoas.map((pessoa) => {
                  const ativa = isPessoaAtiva(pessoa);
                  const isExpanded = expandedIds.includes(pessoa.id);
                  const isToggling = togglingId === pessoa.id;
                  const docClean = pessoa.documento ? pessoa.documento.replace(/\D/g, '') : '';
                  const isPJ = docClean.length > 11;

                  return (
                    <React.Fragment key={pessoa.id}>
                      <tr 
                        className={`transition-colors border-b border-slate-100 ${
                          isExpanded 
                            ? 'bg-red-50/30' 
                            : !ativa 
                              ? 'bg-slate-50/70 opacity-75 hover:bg-slate-100/70' 
                              : 'hover:bg-slate-50'
                        }`}
                      >
                        {/* Down Arrow / Expand Button */}
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => toggleExpand(pessoa.id)}
                            className={`p-1.5 rounded-lg border transition-all cursor-pointer ${
                              isExpanded
                                ? 'bg-red-600 text-white border-red-600 shadow-2xs'
                                : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 hover:text-slate-900'
                            }`}
                            title={isExpanded ? 'Ocultar detalhes' : 'Clique para ver detalhes'}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </td>

                        {/* Name & Document (Reduced width with line break) */}
                        <td className="px-3 py-3 min-w-[200px] max-w-[280px]">
                          <div 
                            className="flex items-start gap-2.5 cursor-pointer group"
                            onClick={() => toggleExpand(pessoa.id)}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 border mt-0.5 ${
                              isPJ 
                                ? 'bg-blue-50 text-blue-700 border-blue-200' 
                                : 'bg-slate-100 text-slate-700 border-slate-200'
                            }`}>
                              {isPJ ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />}
                            </div>
                            <div className="min-w-0 space-y-0.5">
                              {/* Nome com quebra de linha */}
                              <div className="text-sm font-bold text-slate-900 group-hover:text-red-600 transition-colors leading-tight break-words whitespace-normal">
                                {pessoa.nome}
                              </div>
                              {pessoa.nome_fantasia && (
                                <div className="text-xs text-slate-500 leading-tight break-words whitespace-normal">
                                  Fantasia: <span className="font-medium text-slate-700">{pessoa.nome_fantasia}</span>
                                </div>
                              )}
                              {/* Documento abaixo do nome */}
                              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                                <span className="text-xs font-mono font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 shrink-0">
                                  {formatDocumento(pessoa.documento)}
                                </span>
                                <span className={`px-1.5 py-0.2 rounded font-semibold text-[10px] uppercase border shrink-0 ${
                                  isPJ 
                                    ? 'bg-blue-50 text-blue-700 border-blue-200' 
                                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                }`}>
                                  {isPJ ? 'PJ' : 'PF'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Contato */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          <div className="space-y-0.5">
                            {pessoa.fone ? (
                              <div className="text-xs text-slate-800 font-medium flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                {formatPhone(pessoa.fone)}
                              </div>
                            ) : null}
                            {pessoa.email ? (
                              <div className="text-xs text-slate-500 flex items-center gap-1.5 truncate max-w-[200px]">
                                <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span className="truncate">{pessoa.email}</span>
                              </div>
                            ) : null}
                            {!pessoa.fone && !pessoa.email && (
                              <span className="text-xs text-slate-400 italic">- Sem contato -</span>
                            )}
                          </div>
                        </td>

                        {/* Status no Banco */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          {ativa ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                              Ativo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-800 border border-rose-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                              Inativo
                            </span>
                          )}
                        </td>

                        {/* Actions (Sem botão de excluir) */}
                        <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-1">
                            {/* Toggle Ativo Button */}
                            <ProtectedField permission="manage_people" hideMode="remove">
                              <button
                                type="button"
                                onClick={() => handleToggleAtivo(pessoa)}
                                disabled={isToggling}
                                className={`px-2.5 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1 text-xs font-semibold cursor-pointer border ${
                                  ativa
                                    ? 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-200'
                                    : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-200'
                                }`}
                                title={ativa ? 'Clique para Desativar este cadastro' : 'Clique para Ativar este cadastro'}
                              >
                                <Power className={`h-3.5 w-3.5 ${isToggling ? 'animate-spin' : ''}`} />
                                <span className="hidden xl:inline">{ativa ? 'Desativar' : 'Ativar'}</span>
                              </button>
                            </ProtectedField>

                            {/* Detalhes Modal Button */}
                            <button 
                              type="button"
                              onClick={() => handleOpenDetails(pessoa.id)}
                              className="text-slate-500 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-red-200"
                              title="Abrir ficha completa em modal"
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            {/* Editar Button */}
                            <ProtectedField permission="manage_people" hideMode="remove">
                              <button 
                                type="button"
                                onClick={() => handleOpenModal(pessoa)}
                                className="text-slate-600 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors cursor-pointer border border-transparent hover:border-blue-200"
                                title="Editar cadastro"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </ProtectedField>
                          </div>
                        </td>
                      </tr>

                      {/* EXPANDED ACCORDION ROW FOR DETAILS */}
                      {isExpanded && (
                        <tr className="bg-slate-50/90 border-b-2 border-slate-200">
                          <td colSpan={5} className="p-0 max-w-0">
                            <div className="p-4 border-l-4 border-red-600 space-y-3 w-full overflow-x-auto">
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="p-1.5 rounded-lg bg-red-100 text-red-700 font-bold text-xs shrink-0">
                                    <Hash className="w-3.5 h-3.5 inline" /> {pessoa.id}
                                  </div>
                                  <div className="min-w-0">
                                    <h3 className="text-sm font-bold text-slate-900 truncate">{pessoa.nome}</h3>
                                    {pessoa.nome_fantasia && (
                                      <p className="text-xs text-slate-500 font-medium truncate">Fantasia: {pessoa.nome_fantasia}</p>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenDetails(pessoa.id)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-white text-slate-800 border border-slate-300 hover:bg-slate-100 transition-colors shadow-2xs cursor-pointer"
                                  >
                                    <Eye className="w-3.5 h-3.5 text-red-600" />
                                    Ver Ficha
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenModal(pessoa)}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-2xs cursor-pointer"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => toggleExpand(pessoa.id)}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer"
                                    title="Fechar detalhes inline"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              {/* Details Grid */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                                {/* Coluna 1: Documentação */}
                                <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-1.5 min-w-0">
                                  <div className="font-semibold text-slate-500 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-1">
                                    <FileText className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                    <span>Documentação</span>
                                  </div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="truncate">
                                      <span className="text-slate-400 font-medium">CPF/CNPJ:</span>{' '}
                                      <span className="font-bold font-mono text-slate-800">{formatDocumento(pessoa.documento)}</span>
                                    </div>
                                    <div className="truncate">
                                      <span className="text-slate-400 font-medium">Situação:</span>{' '}
                                      <span className="font-semibold text-slate-700">{pessoa.situacao_cadastral || (ativa ? 'ATIVO' : 'INATIVO')}</span>
                                    </div>
                                    {pessoa.cnae && (
                                      <div className="truncate">
                                        <span className="text-slate-400 font-medium">CNAE:</span>{' '}
                                        <span className="text-slate-700">{pessoa.cnae}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Coluna 2: Contato Direct */}
                                <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-1.5 min-w-0">
                                  <div className="font-semibold text-slate-500 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-1">
                                    <Phone className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                    <span>Contato</span>
                                  </div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="flex items-center gap-1.5 truncate">
                                      <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                      <span className="text-slate-400 font-medium shrink-0">Fone:</span>{' '}
                                      <span className="font-bold text-slate-800 truncate">{formatPhone(pessoa.fone)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 truncate">
                                      <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                      <span className="text-slate-400 font-medium shrink-0">Email:</span>{' '}
                                      <span className="text-slate-800 truncate">{pessoa.email || '-'}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Coluna 3: Histórico e Obs */}
                                <div className="bg-white p-3 rounded-lg border border-slate-200 space-y-1.5 min-w-0 sm:col-span-2 lg:col-span-1">
                                  <div className="font-semibold text-slate-500 uppercase tracking-wider text-[11px] flex items-center gap-1.5 border-b pb-1">
                                    <Calendar className="w-3.5 h-3.5 text-red-600 shrink-0" />
                                    <span>Histórico & Obs</span>
                                  </div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="truncate">
                                      <span className="text-slate-400 font-medium">Cadastro:</span>{' '}
                                      <span className="text-slate-700">
                                        {pessoa.created_at ? (
                                          new Date(pessoa.created_at).toLocaleDateString('pt-BR')
                                        ) : (
                                          <span className="text-slate-400 italic">Legado (sem registro)</span>
                                        )}
                                      </span>
                                    </div>
                                    {pessoa.obs && (
                                      <div className="bg-slate-50 p-1.5 rounded border border-slate-200 text-slate-700 italic truncate">
                                        "{pessoa.obs}"
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION BAR (Matching User's Screenshot) */}
        {!loading && totalRecords > 0 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Left: Select items per page */}
            <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
              <span>Exibir:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-white border border-slate-300 text-slate-800 text-xs font-semibold rounded-lg px-2.5 py-1.5 focus:border-red-500 focus:outline-none shadow-2xs cursor-pointer"
              >
                <option value={10}>10 por página</option>
                <option value={25}>25 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>

            {/* Center: Record info text "1 a 50 de 7046 registros" */}
            <div className="text-xs font-medium text-slate-600 text-center">
              {startRecord} a {endRecord} de {totalRecords} registros
            </div>

            {/* Right: Numeric Pagination Controls */}
            <div className="inline-flex items-center -space-x-px rounded-lg border border-slate-300 bg-white shadow-2xs overflow-hidden text-xs">
              {/* First Page button « */}
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors border-r border-slate-200 cursor-pointer disabled:cursor-not-allowed"
                title="Primeira página"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>

              {/* Prev Page button < */}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors border-r border-slate-200 cursor-pointer disabled:cursor-not-allowed"
                title="Página anterior"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {/* Page numbers */}
              {getPageNumbers(currentPage, totalPages, 10).map((pageNum) => {
                const isActive = pageNum === currentPage;
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 font-bold transition-colors border-r border-slate-200 cursor-pointer ${
                      isActive
                        ? 'bg-red-600 text-white border-red-600'
                        : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900 bg-white'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {/* Next Page button > */}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-2.5 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors border-r border-slate-200 cursor-pointer disabled:cursor-not-allowed"
                title="Próxima página"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>

              {/* Last Page button » */}
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-2.5 py-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition-colors cursor-pointer disabled:cursor-not-allowed"
                title="Última página"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <PessoasForm 
          pessoaId={editingId} 
          onClose={handleCloseModal} 
          onSave={handleSave} 
        />
      )}

      {isDetailsOpen && viewingId && (
        <PessoaDetailsModal
          pessoaId={viewingId}
          onClose={handleCloseDetails}
          onEdit={handleEditFromDetails}
        />
      )}
    </div>
  );
}

